import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, FileText, Upload, Pencil, Trash2, ExternalLink, Sparkles, Brain } from 'lucide-react';
import { toast } from 'sonner';
import apiClient, { API_URL } from '../services/apiClient';
import { Button, Badge, Modal, Input, Textarea, SelectField, PageHeader, EmptyState, ConfirmDialog, Spinner } from '../components/ui';
import type { Paper } from '@conference/shared';

// ─── Schema ───────────────────────────────────────────────────────────────────

const paperSchema = z.object({
  title:              z.string().min(3, 'Title is required'),
  authors:            z.string().min(2, 'At least one author required'),
  conference:         z.string().optional(),
  paper_number:       z.string().optional(),
  abstract:           z.string().optional(),
  session_id:         z.string().optional(),
  is_featured:        z.boolean().default(false),
  featured_order:     z.string().optional(),
});
type PaperForm = z.input<typeof paperSchema>;

// ─── Processing status badge ───────────────────────────────────────────────────

type ProcessingStatus = 'pending' | 'processing' | 'processed' | 'failed';

function ProcessingStatusBadge({ status, error }: { status?: ProcessingStatus | null; error?: string | null }) {
  if (!status || status === 'pending') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-300">
        Pending
      </span>
    );
  }
  if (status === 'processing') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/20 text-yellow-300">
        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Processing...
      </span>
    );
  }
  if (status === 'processed') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-300">
        Ready
      </span>
    );
  }
  // failed
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-300 cursor-help"
      title={error ?? 'Processing failed'}
    >
      Failed
    </span>
  );
}

// ─── Paper modal ──────────────────────────────────────────────────────────────

function PaperModal({ open, onClose, paper, eventId, sessions, onSaved }: {
  open: boolean; onClose: () => void; paper: Paper | null;
  eventId: string; sessions: { id: string; title: string }[]; onSaved: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<PaperForm>({
    resolver: zodResolver(paperSchema),
    defaultValues: { title: '', authors: '', conference: '', paper_number: '', abstract: '', session_id: '', is_featured: false, featured_order: '' },
  });
  const [pendingSave, setPendingSave] = useState<PaperForm | null>(null);
  const isFeatured = watch('is_featured');

  useEffect(() => {
    if (open) {
      setPdfUrl(paper?.pdf_url ?? null);
      reset(paper ? {
        title:              paper.title,
        authors:            paper.authors.join(', '),
        conference:         paper.conference ?? '',
        paper_number:       paper.paper_number ?? '',
        abstract:           paper.abstract ?? '',
        session_id:         paper.session_id ?? '',
        is_featured:        paper.is_featured ?? false,
        featured_order:     paper.featured_order?.toString() ?? '',
      } : { title: '', authors: '', conference: '', paper_number: '', abstract: '', session_id: '', is_featured: false, featured_order: '' });
    }
  }, [open, paper, reset]);

  async function handleFileUpload(file: File) {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await apiClient.upload('/files/upload', formData);
      setPdfUrl(result.path);
      toast.success('PDF uploaded');
    } catch (err) {
      toast.error('Upload failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setUploading(false);
    }
  }

  const persist = async (data: PaperForm) => {
    const payload = {
      event_id:           eventId,
      title:              data.title,
      authors:            data.authors.split(',').map((a) => a.trim()).filter(Boolean),
      conference:         data.conference   || null,
      paper_number:       data.paper_number || null,
      abstract:           data.abstract     || null,
      session_id:         data.session_id   || null,
      is_featured:        data.is_featured ?? false,
      featured_order:     data.is_featured && data.featured_order ? Number(data.featured_order) : null,
      pdf_url:            pdfUrl,
    };

    try {
      if (paper) {
        await apiClient.patch(`/papers/${paper.id}`, payload);
      } else {
        await apiClient.post('/papers', payload);
      }
      toast.success(paper ? 'Paper updated' : 'Paper added');
      setPendingSave(null); onSaved(); onClose();
    } catch (err) {
      toast.error('Save failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    }
  };
  const prepareSave = (data: PaperForm) => paper ? setPendingSave(data) : persist(data);

  return (
    <Modal open={open} onClose={onClose} title={paper ? 'Edit Paper' : 'Add Paper'} size="xl">
      <form onSubmit={handleSubmit(prepareSave)} className="space-y-4">
        <Input label="Title *" placeholder="Quantum-Enhanced Neural Networks…" error={errors.title?.message} {...register('title')} />
        <Input
          label="Authors * (comma-separated)"
          placeholder="Dr. Elena Rodriguez, Dr. Marcus Chen"
          error={errors.authors?.message}
          {...register('authors')}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Conference / Venue" placeholder="NeurIPS 2025" {...register('conference')} />
          <Input label="Paper Number / ID" placeholder="#4092" {...register('paper_number')} />
        </div>
        <Textarea label="Abstract" placeholder="This paper presents…" rows={4} {...register('abstract')} />
        <SelectField
          label="Assign to Session"
          options={[{ value: '', label: '— None —' }, ...sessions.map((s) => ({ value: s.id, label: s.title }))]}
          {...register('session_id')}
        />

        {/* PDF Upload */}
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-gray-300">PDF File</p>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
            />
            <Button
              type="button"
              variant="secondary"
              icon={uploading ? <Spinner className="w-3.5 h-3.5" /> : <Upload size={14} />}
              loading={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {pdfUrl ? 'Replace PDF' : 'Upload PDF'}
            </Button>
            {pdfUrl && (
              <a
                href={pdfUrl.startsWith('http') ? pdfUrl : `${API_URL}/files/${pdfUrl}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-sky-400 hover:text-sky-300">
                <ExternalLink size={12} /> View uploaded PDF
              </a>
            )}
          </div>
          <p className="text-xs text-gray-500">
            PDF is stored on the server. After saving, click the ✦ button on the paper row to process it for AI search.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="is_featured" className="accent-sky-500" {...register('is_featured')} />
          <label htmlFor="is_featured" className="text-sm text-gray-300">Feature on home page</label>
        </div>
        {isFeatured && <Input label="Featured order" type="number" min="0" placeholder="Optional" {...register('featured_order')} />}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={isSubmitting}>{paper ? 'Save Changes' : 'Add Paper'}</Button>
        </div>
      </form>
      <ConfirmDialog open={pendingSave !== null} onClose={() => setPendingSave(null)} onConfirm={() => pendingSave && persist(pendingSave)} intent="primary" title="Save changes?" description="Apply the current paper changes?" confirmLabel="Save changes" />
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PapersPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [papers, setPapers]               = useState<Paper[]>([]);
  const [sessions, setSessions]           = useState<{ id: string; title: string }[]>([]);
  const [eventName, setEventName]         = useState('');
  const [loading, setLoading]             = useState(true);
  const [search, setSearch]               = useState('');
  const [modalOpen, setModal]             = useState(false);
  const [editing, setEditing]             = useState<Paper | null>(null);
  const [deleteTarget, setDel]            = useState<Paper | null>(null);
  const [deleting, setDeleting]           = useState(false);
  const [processingId, setProc]           = useState<string | null>(null);
  const [embedding, setEmbedding]         = useState(false);
  const [selectedPaperIds, setSelectedPaperIds] = useState<string[]>([]);
  const [isProcessing, setIsProcessing]   = useState(false);
  const [extraction, setExtraction] = useState<{ enabled: boolean; configured: boolean; queue_available: boolean; message: string } | null>(null);
  const canExtract = !!(extraction?.enabled && extraction.configured && extraction.queue_available);
  async function checkExtraction() {
    if (!eventId) return;
    setExtraction(await apiClient.get<{ enabled: boolean; configured: boolean; queue_available: boolean; message: string }>(
      `/admin/events/${eventId}/extraction-status`).catch(() => ({ enabled: false, configured: false, queue_available: false, message: 'PDF extraction is unavailable. Contact your administrator.' })));
  }

  async function handleProcessPaper(paper: Paper) {
    if (!canExtract) return;
    if (!paper.pdf_url) {
      toast.error('No PDF to process', { description: 'Upload a PDF first. Abstracts can be indexed without PDF extraction.' });
      return;
    }
    setProc(paper.id);
    setPapers(prev => prev.map(p => p.id === paper.id ? { ...p, processing_status: 'processing' } : p));
    try {
      await apiClient.post('/admin/process-paper', { paperId: paper.id });
      toast.success('Paper queued for processing');
    } catch (err) {
      toast.error('AI processing failed', { description: err instanceof Error ? err.message : 'Unknown error' });
      void checkExtraction();
    } finally {
      // Refetch this paper's status
      const updated = eventId
        ? await apiClient.get<Paper>(`/papers/${paper.id}?event_id=${encodeURIComponent(eventId)}`).catch(() => null)
        : null;
      if (updated) {
        setPapers(prev => prev.map(p => p.id === paper.id ? { ...p, ...updated } : p));
      }
      setProc(null);
    }
  }

  async function handleBatchProcess() {
    if (!canExtract) return;
    setIsProcessing(true);
    const ids = [...selectedPaperIds];
    let queued = 0;
    for (const pid of ids) {
      setPapers(prev => prev.map(p => p.id === pid ? { ...p, processing_status: 'processing' } : p));
      try { await apiClient.post('/admin/process-paper', { paperId: pid }); queued++; }
      catch { /* Summarize failures accurately below; no false success. */ }
      const updated = eventId
        ? await apiClient.get<Paper>(`/papers/${pid}?event_id=${encodeURIComponent(eventId)}`).catch(() => null)
        : null;
      if (updated) {
        setPapers(prev => prev.map(p => p.id === pid ? { ...p, ...updated } : p));
      }
    }
    setSelectedPaperIds([]);
    setIsProcessing(false);
    if (queued) toast.success(`Queued ${queued} paper${queued !== 1 ? 's' : ''}`);
    if (queued < ids.length) toast.error(`${ids.length - queued} paper(s) could not be queued. Check PDF availability and extraction status.`);
    void checkExtraction();
  }

  async function handleEmbedAll() {
    if (!eventId) return;
    setEmbedding(true);
    try {
      const data = await apiClient.post<{ embedded: Record<string, number>; total: number }>(
        '/admin/embed-content', { eventId }
      );
      const { embedded } = data;
      toast.success('AI knowledge base updated', {
        description: `Embedded: ${embedded.sessions ?? 0} sessions, ${embedded.speakers ?? 0} speakers, ${embedded.exhibitors ?? 0} exhibitors`,
      });
    } catch (err) {
      toast.error('Embedding failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setEmbedding(false);
    }
  }

  async function load() {
    if (!eventId) return;
    setLoading(true);
    try {
      const [eventData, papersData, sessionsData] = await Promise.all([
        apiClient.get<{ id: string; name: string }>(`/admin/events/${eventId}`),
        apiClient.get<Paper[]>(`/papers?event_id=${eventId}`),
        apiClient.get<{ id: string; title: string }[]>(`/sessions?event_id=${eventId}`),
      ]);
      setEventName(eventData.name);
      setPapers(papersData);
      setSessions(sessionsData);
      setSelectedPaperIds([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { setExtraction(null); load(); void checkExtraction(); }, [eventId]);
  const pendingWork = papers.some(p => p.processing_status === 'processing' || ['pending', 'indexing'].includes(p.indexing_status ?? '') || ['pending', 'generating'].includes(p.insight_status ?? ''));
  useEffect(() => {
    if (!pendingWork || !eventId) return;
    let active = true;
    const interval = window.setInterval(() => {
      apiClient.get<Paper[]>(`/papers?event_id=${eventId}`).then(rows => { if (active) setPapers(rows); }).catch(() => {});
    }, 5000);
    return () => { active = false; window.clearInterval(interval); };
  }, [pendingWork, eventId]);

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/papers/${deleteTarget.id}`);
      toast.success('Paper deleted');
      setDel(null);
      load();
    } catch (err) {
      toast.error('Delete failed', { description: err instanceof Error ? err.message : 'Unknown error' });
    } finally {
      setDeleting(false);
    }
  }

  const filtered = search
    ? papers.filter((p) =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        p.authors.some((a) => a.toLowerCase().includes(search.toLowerCase())) ||
        p.conference?.toLowerCase().includes(search.toLowerCase())
      )
    : papers;

  function toggleSelect(id: string) {
    setSelectedPaperIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  function toggleSelectAll() {
    if (selectedPaperIds.length === filtered.length) {
      setSelectedPaperIds([]);
    } else {
      setSelectedPaperIds(filtered.map(p => p.id));
    }
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Papers"
        description={`Manage research papers for ${eventName || '…'}`}
        breadcrumb={<Link to="/events" className="hover:text-gray-300">Events</Link>}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={handleBatchProcess}
              disabled={!canExtract || selectedPaperIds.length === 0 || isProcessing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-500/10 text-amber-300 border border-amber-500/30 hover:bg-amber-500/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {isProcessing ? 'Processing...' : `Process Selected (${selectedPaperIds.length})`}
            </button>
            <Button
              variant="secondary"
              icon={embedding ? <Spinner className="w-3.5 h-3.5" /> : <Brain size={14} />}
              loading={embedding}
              onClick={handleEmbedAll}
              title="Generate AI embeddings for all sessions, speakers, and exhibitors in this event"
            >
              Embed All Content
            </Button>
            <Button icon={<Plus size={14} />} onClick={() => { setEditing(null); setModal(true); }}>Add Paper</Button>
          </div>
        }
      />

      {!canExtract && <div role="status" className="mb-5 text-sm text-amber-300">
        {extraction?.message || (extraction ? 'PDF extraction is unavailable.' : 'Checking PDF extraction availability…')}
        <button type="button" className="ml-3 underline min-h-11" onClick={() => void checkExtraction()}>Check again</button>
      </div>}

      <div className="mb-5">
        <input
          type="search" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search papers, authors, conferences…"
          className="w-full max-w-sm px-3.5 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner className="w-6 h-6 text-gray-500" /></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No papers yet"
          description="Add research papers for attendees to browse."
          action={<Button icon={<Plus size={14} />} onClick={() => setModal(true)}>Add Paper</Button>}
        />
      ) : (
        <>
          {filtered.length > 1 && (
            <div className="flex items-center gap-2 mb-2 px-1">
              <input
                type="checkbox"
                className="accent-sky-500 cursor-pointer"
                checked={selectedPaperIds.length === filtered.length}
                onChange={toggleSelectAll}
                title="Select all"
              />
              <span className="text-xs text-gray-500">
                {selectedPaperIds.length > 0
                  ? `${selectedPaperIds.length} of ${filtered.length} selected`
                  : 'Select all'}
              </span>
            </div>
          )}
          <div className="space-y-2">
            {filtered.map((paper) => (
              <div key={paper.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
                <div className="flex items-start gap-3">
                  {/* Row checkbox */}
                  <div className="flex items-center pt-0.5">
                    <input
                      type="checkbox"
                      className="accent-sky-500 cursor-pointer"
                      checked={selectedPaperIds.includes(paper.id)}
                      onChange={() => toggleSelect(paper.id)}
                    />
                  </div>
                  <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <FileText size={15} className="text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <p className="text-sm font-medium text-white truncate">{paper.title}</p>
                      {paper.is_featured && <Badge variant="warning">Featured</Badge>}
                      {paper.pdf_url && <Badge variant="success">PDF</Badge>}
                      <ProcessingStatusBadge
                        status={paper.processing_status}
                        error={paper.processing_error}
                      />
                    </div>
                    {paper.processing_warning && <p className="text-xs text-amber-300 mt-1">{paper.processing_warning}</p>}
                    {paper.indexing_status && <p className="text-xs text-gray-400 mt-1">
                      {paper.indexing_status === 'indexed' ? 'Search ready' : paper.indexing_status === 'failed' ? 'Search indexing failed' : 'Preparing searchable content…'}
                    </p>}
                    {paper.indexing_status === 'failed' && <div className="text-xs text-amber-300">
                      {paper.indexing_error}
                      <button type="button" className="ml-2 underline min-h-11" onClick={async () => {
                        try { await apiClient.post(`/admin/papers/${paper.id}/retry-indexing`, {}); toast.success('Search indexing queued; PDF will not be reprocessed'); load(); }
                        catch { toast.error('Could not queue search indexing'); }
                      }}>Retry search indexing</button>
                    </div>}
                    {paper.insight_status && <p className="text-xs text-gray-400 mt-1">
                      {paper.insight_status === 'ready' ? 'AI insight ready' : paper.insight_status === 'skipped' ? 'AI insight disabled for this event' : paper.insight_status === 'failed' ? 'AI insight failed; search is unaffected' : 'Preparing optional AI insight…'}
                    </p>}
                    {paper.insight_status === 'failed' && <div className="text-xs text-amber-300">
                      {paper.insight_error}
                      <button type="button" className="ml-2 underline min-h-11" onClick={async () => {
                        try { await apiClient.post(`/admin/papers/${paper.id}/retry-insight`, {}); toast.success('Insight queued; PDF and search will not be reprocessed'); load(); }
                        catch { toast.error('Could not queue insight generation'); }
                      }}>Retry AI insight</button>
                    </div>}
                    <p className="text-xs text-gray-400">{paper.authors.join(', ')}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      {paper.conference && <span>{paper.conference}</span>}
                      {paper.paper_number && <span>{paper.paper_number}</span>}
                    </div>
                    {paper.abstract && (
                      <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{paper.abstract}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {paper.pdf_url && (
                      <a
                        href={paper.pdf_url.startsWith('http') ? paper.pdf_url : `${API_URL}/files/${paper.pdf_url}`}
                        target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm" icon={<ExternalLink size={12} />} />
                      </a>
                    )}
                    <Button
                      variant="ghost" size="sm"
                      icon={processingId === paper.id ? <Spinner className="w-3 h-3" /> : <Sparkles size={12} />}
                      title="Process paper with AI — extracts text and creates searchable embeddings"
                      loading={processingId === paper.id}
                      disabled={!canExtract || !paper.pdf_url || paper.processing_status === 'processing'}
                      className={canExtract && paper.pdf_url ? 'hover:text-amber-400' : 'opacity-30 cursor-not-allowed'}
                      onClick={() => handleProcessPaper(paper)}
                    />
                    <Button variant="ghost" size="sm" icon={<Pencil size={12} />} onClick={() => { setEditing(paper); setModal(true); }} />
                    <Button variant="ghost" size="sm" icon={<Trash2 size={12} />} className="hover:text-red-400" onClick={() => setDel(paper)} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <PaperModal open={modalOpen} onClose={() => setModal(false)} paper={editing} eventId={eventId ?? ''} sessions={sessions} onSaved={load} />
      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDel(null)} onConfirm={handleDelete} loading={deleting}
        title="Delete Paper" description={`Delete "${deleteTarget?.title}"?`}
      />
    </div>
  );
}
